/**
 * connectSources — the single taxonomy for WHERE a SLIK Connect message came from. (Jose 2026-10-30)
 *
 * Every outbound message carries a `source`; inbound client replies are 'inbound'. Each source rolls up
 * into ONE of four categories the inbox filters/groups by. This is the shared source of truth for both the
 * service (what gets stamped on the message doc) and the UI (the chip label + the filter pills).
 *
 * DOCTRINE: a message's label = its source; the inbox groups by the source's category. Never infer the
 * origin from the body text — stamp `source` at the send site.
 */

export type ConnectSource =
  | 'manual'              // a staff member typed it (ConnectCenter / profile / appointment / "Client message from X")
  | 'waitlist'            // "can we squeeze you in?" outreach from the waitlist
  | 'reschedule'          // a time-change / alternative-time offer
  | 'marketing'           // a campaign / blast
  | 'review_request'      // a feedback / rating ask
  | 'checkout_receipt'    // the post-visit receipt + thank-you
  | 'reminder'            // an appointment reminder (24h / 1h)
  | 'confirmation'        // a booking confirmation
  | 'emergency'           // emergency-booking coordination (counter-offer, confirm)
  | 'auto_reply'          // the Layer-1 inbound auto-reply
  | 'barber_notification' // a heads-up sent to a barber (not a client)
  | 'payment_link'        // a Stripe pay-link texted to the client
  | 'card_request'        // "add your card on file" link texted from the appointment card
  | 'inbound';            // a client-initiated reply (direction 'in')

export type ConnectCategory = 'needs_reply' | 'outreach' | 'transactional' | 'operational';

export interface SourceMeta {
  /** Short chip text shown on the message + in the list row. */
  label: string;
  /** Which filter pill this rolls up under. */
  category: ConnectCategory;
  /** Tailwind chip classes (bg + text), tuned for light & dark. */
  tone: string;
}

export const CATEGORY_META: Record<ConnectCategory, { label: string; icon: string; tone: string }> = {
  needs_reply:   { label: 'Needs reply',   icon: '🔴', tone: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
  outreach:      { label: 'Outreach',      icon: '💬', tone: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  transactional: { label: 'Transactional', icon: '🧾', tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  operational:   { label: 'Operational',   icon: '⚙️', tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
};

const META: Record<ConnectSource, SourceMeta> = {
  manual:              { label: 'Staff message', category: 'operational',   tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  waitlist:            { label: 'Waitlist',      category: 'outreach',      tone: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  reschedule:          { label: 'Reschedule',    category: 'outreach',      tone: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  marketing:           { label: 'Marketing',     category: 'outreach',      tone: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  review_request:      { label: 'Review ask',    category: 'outreach',      tone: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  checkout_receipt:    { label: 'Receipt',       category: 'transactional', tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  reminder:            { label: 'Reminder',      category: 'transactional', tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  confirmation:        { label: 'Confirmation',  category: 'transactional', tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  emergency:           { label: 'Emergency',     category: 'operational',   tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  auto_reply:          { label: 'Auto-reply',    category: 'operational',   tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  barber_notification: { label: 'Barber alert',  category: 'operational',   tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  payment_link:        { label: 'Payment link',  category: 'transactional', tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  card_request:        { label: 'Add-card link', category: 'transactional', tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  inbound:             { label: 'Client reply',  category: 'needs_reply',   tone: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
};

/**
 * Resolve a message's label/category. An inbound message is always treated as 'inbound' (a client reply)
 * regardless of any stamped source; an outbound with no source falls back to 'manual'.
 */
export function sourceMeta(source?: ConnectSource, direction?: 'in' | 'out'): SourceMeta {
  if (direction === 'in') return META.inbound;
  return META[(source as ConnectSource)] || META.manual;
}

/** A "staff message from <name>" label when we know who typed it (manual sources only). */
export function labelFor(source?: ConnectSource, direction?: 'in' | 'out', byName?: string): string {
  const m = sourceMeta(source, direction);
  if ((source === 'manual' || !source) && direction !== 'in' && byName) return `Message from ${byName.split(' ')[0]}`;
  return m.label;
}
